import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, Select, Checkbox, Loading, ErrorNote,
    confirmAction, toast
} from '../components/ui';
import { DataTable } from '../components/DataTable';

interface User {
    id: number;
    userName: string;
    email: string;
    fullName: string | null;
    roleId: number;
    isActive: boolean;
    isLocked: boolean;
    lastLoginAt: string | null;
    mustChangePassword: boolean;
    role: { id: number; roleCode: string; roleName: string } | null;
}

interface RolesPayload {
    roles: { id: number; roleCode: string; roleName: string; rightsCsv: string; isSystemRole: boolean }[];
    allRights: string[];
}

const EMPTY = {
    id: undefined as number | undefined,
    userName: '', email: '', fullName: '', roleId: '', isActive: true, resetPassword: false
};

/** Plain-English names for the right codes, so an administrator is not reading constants. */
const RIGHT_LABELS: Record<string, string> = {
    VIEW: 'See candidates, interviews and reports',
    CREATE: 'Add candidates and issue interviews',
    UPDATE: 'Change existing records',
    DELETE: 'Remove records',
    EXPORT: 'Export data',
    IMPORT: 'Import candidates from a file',
    REVIEW: 'Approve or reject a completed interview',
    CONFIGURE: 'Change questions, templates, proctoring and branding',
    MANAGE_USERS: 'Manage users, roles, API keys and webhooks',
    VIEW_RECORDING: 'Watch a candidate recording'
};

export function Users() {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState<ApiError | null>(null);
    const [oneTimePassword, setOneTimePassword] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const users = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get<User[]>('/api/admin/users')
    });

    const roles = useQuery({
        queryKey: ['roles'],
        queryFn: () => api.get<RolesPayload>('/api/admin/roles')
    });

    const save = useMutation({
        mutationFn: () => api.post<{ oneTimePassword: string | null }>('/api/admin/users', {
            ...form, roleId: Number(form.roleId)
        }),
        onSuccess: async (result) => {
            // Shown once, here, and never stored in readable form. Put where it can be copied rather
            // than announced in a toast that vanishes.
            setOneTimePassword(result.oneTimePassword);
            setForm(EMPTY);
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['users'] });
            if (!result.oneTimePassword) toast('User saved.');
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const unlock = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/users/${id}/unlock`),
        onSuccess: async () => {
            toast('That account can sign in again.');
            await queryClient.invalidateQueries({ queryKey: ['users'] });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const saveRole = useMutation({
        mutationFn: (input: { id: number; rightsCsv: string }) =>
            api.post(`/api/admin/roles/${input.id}`, { rightsCsv: input.rightsCsv }),
        onSuccess: async () => {
            toast('Role updated.');
            await queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const toggleRight = (roleId: number, current: string, right: string) => {
        const rights = current.split(',').map((r) => r.trim()).filter(Boolean);
        const next = rights.includes(right)
            ? rights.filter((r) => r !== right)
            : [...rights, right];

        saveRole.mutate({ id: roleId, rightsCsv: next.join(',') });
    };

    return (
        <div className="space-y-6">
            {oneTimePassword && (
                <Card title="One-time password">
                    <p className="mb-2 text-sm text-slate-600">
                        Copy this now — it is not stored anywhere and cannot be shown again. The user
                        must change it when they first sign in.
                    </p>
                    <div className="flex gap-2">
                        <TextInput readOnly value={oneTimePassword} onFocus={(e) => e.target.select()} />
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={async () => {
                                await navigator.clipboard.writeText(oneTimePassword);
                                toast('Copied.');
                            }}
                        >
                            Copy
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => setOneTimePassword(null)}>
                            Done
                        </Button>
                    </div>
                </Card>
            )}

            <Card
                heading
                title="Team & access"
                lede="Rights are carried in a signed token, so a change takes effect the next time an affected user signs in."
            >
                {users.isLoading ? <Loading /> : (
                    <DataTable
                        rows={users.data ?? []}
                        rowKey={(row) => row.id}
                        empty="No users yet."
                        columns={[
                            { header: 'Username', sortBy: (row) => row.userName, cell: (row) => <span className="font-medium">{row.userName}</span> },
                            { header: 'Name', sortBy: (row) => row.fullName, cell: (row) => row.fullName ?? '—' },
                            { header: 'Email', sortBy: (row) => row.email, cell: (row) => row.email },
                            { header: 'Role', sortBy: (row) => row.role?.roleName ?? null, cell: (row) => row.role?.roleName ?? '—' },
                            {
                                header: 'State',
                                cell: (row) => !row.isActive ? 'Disabled'
                                    : row.isLocked ? 'Locked'
                                        : row.mustChangePassword ? 'Must change password'
                                            : 'Active'
                            },
                            {
                                header: '',
                                className: 'text-right',
                                cell: (row) => (
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setForm({
                                                id: row.id,
                                                userName: row.userName,
                                                email: row.email,
                                                fullName: row.fullName ?? '',
                                                roleId: String(row.roleId),
                                                isActive: row.isActive,
                                                resetPassword: false
                                            })}
                                        >
                                            Edit
                                        </Button>
                                        {row.isLocked && (
                                            <Button variant="secondary" onClick={() => unlock.mutate(row.id)}>
                                                Unlock
                                            </Button>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                    />
                )}
            </Card>

            <div className="grid gap-6 lg:grid-cols-2">
                <Card title={form.id ? `Edit ${form.userName}` : 'Add a user'}>
                    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
                        {error && !error.errors && <ErrorNote message={error.message} />}

                        <Field label="Username" error={error?.fieldError('userName')}>
                            <TextInput
                                value={form.userName}
                                onChange={(e) => setForm((f) => ({ ...f, userName: e.target.value }))}
                                invalid={!!error?.fieldError('userName')}
                            />
                        </Field>

                        <Field label="Email" error={error?.fieldError('email')}>
                            <TextInput
                                value={form.email}
                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                invalid={!!error?.fieldError('email')}
                            />
                        </Field>

                        <Field label="Full name">
                            <TextInput
                                value={form.fullName}
                                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                            />
                        </Field>

                        <Field label="Role" error={error?.fieldError('roleId')}>
                            <Select
                                value={form.roleId}
                                onChange={(e) => setForm((f) => ({ ...f, roleId: e.target.value }))}
                                invalid={!!error?.fieldError('roleId')}
                            >
                                <option value="">Choose a role…</option>
                                {(roles.data?.roles ?? []).map((r) => (
                                    <option key={r.id} value={r.id}>{r.roleName}</option>
                                ))}
                            </Select>
                        </Field>

                        <Checkbox
                            label="Account is active"
                            hint="A disabled account cannot sign in but keeps its history."
                            checked={form.isActive}
                            onChange={(checked) => setForm((f) => ({ ...f, isActive: checked }))}
                        />

                        {form.id && (
                            <Checkbox
                                label="Give them a new one-time password"
                                hint="Signs them out everywhere and forces a change at next sign-in."
                                checked={form.resetPassword}
                                onChange={(checked) => setForm((f) => ({ ...f, resetPassword: checked }))}
                            />
                        )}

                        <div className="flex gap-2">
                            <Button type="submit" busy={save.isPending}>Save</Button>
                            {form.id && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => { setForm(EMPTY); setError(null); }}
                                >
                                    Cancel
                                </Button>
                            )}
                        </div>
                    </form>
                </Card>

                <Card title="What each role can do">
                    {roles.isLoading ? <Loading /> : (
                        <div className="space-y-5">
                            {(roles.data?.roles ?? []).map((role) => {
                                const rights = role.rightsCsv.split(',').map((r) => r.trim());
                                const isAdmin = rights.includes('*');

                                return (
                                    <div key={role.id}>
                                        <h3 className="mb-2 text-sm font-semibold text-slate-700">
                                            {role.roleName}
                                        </h3>

                                        {isAdmin ? (
                                            <p className="text-sm text-slate-600">
                                                Full access to everything. This cannot be reduced —
                                                otherwise nobody would be left able to grant it back.
                                            </p>
                                        ) : (
                                            <div className="space-y-1.5">
                                                {(roles.data?.allRights ?? []).map((right) => (
                                                    <Checkbox
                                                        key={right}
                                                        label={RIGHT_LABELS[right] ?? right}
                                                        checked={rights.includes(right)}
                                                        onChange={() =>
                                                            toggleRight(role.id, role.rightsCsv, right)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
